/**
 * Line items (a purchase order's items, and later any other document's): the shape of one line and THE calculation of money, in one place.
 * The Line Items Section (styles/LineItemsSection.tsx), the form that owns it and what is saved all call these functions, so the amount on screen
 * and the amount the server stores cannot disagree. The server repeats the arithmetic (backend modules/cost-management/po-lines.js); the
 * same vectors run against both.
 *
 * Money is exact: quantity and price carry up to 4 decimals, their product is computed in integers and rounded to whole CENTS (half a cent
 * rounds up). A new line starts at price 0 and is counted like any other (0 x quantity = 0); a blank price box counts as 0 too.
 */
import { requiredFieldMessage } from '@/styles/GenericForm';

export type PriceSource = 'catalog' | 'manual';

export interface LineItem {
  /** stable React key (a new line has no id) */
  key: string;
  /** the catalog item the line was picked from; null = free text */
  catalogItemId: string | null;
  name: string;
  /** the line's cost code, by id ('' = none chosen). The label shown is the company's current one; it is the id that is saved. */
  costCodeId: string;
  description: string;
  unit: string;
  /** text typed in the table (numbers as text so a half-typed "1." is not rewritten under the user's fingers) */
  quantity: string;
  unitPrice: string;
  /** where the price came from: the vendor's offer ('catalog'), typed by hand ('manual'), or null (the starting 0) */
  priceSource: PriceSource | null;
  /** the vendor whose price this line uses (an offer of the catalog item); null = none / typed by hand */
  vendorId: string | null;
  /** where that vendor came from: the rules for a new line ('auto', the default) or the user's own pick in the line's Vendor box ('manual': a change of the order's vendor does not override it) */
  vendorChoice?: 'auto' | 'manual';
  /**
   * What the catalog item said when the line was linked to it (or when the saved line was opened): the line stays linked while its name,
   * cost code and unit price still equal this. null on a custom line. Never saved: it is derived each time the order opens.
   */
  linkBaseline: { name: string; costCodeId: string; unitPrice: number } | null;
  /** this session the line was edited away from its catalog item (it is now a custom line); shows the informative note */
  unlinkedNotice: boolean;
  /** Bill lines only: the BILL AMOUNT -- what the vendor billed for this line (text; '' on a purchase order line). It can be part of the line's own Amount. */
  billAmount: string;
  /** Bill lines only: the purchase-order line this line covers (what bills billed of each order line adds up through it); null = a plain line */
  sourceLineId: string | null;
}

let lineCounter = 0;
export const newLineKey = (): string => `line-${++lineCounter}`;

/** A new, blank line. `costCodeId` is the company's default cost code (resolved by the caller from the company's real records). */
export const emptyLine = (costCodeId = ''): LineItem => ({
  key: newLineKey(), catalogItemId: null, name: '', costCodeId, description: '', unit: '', quantity: '1', unitPrice: '0', priceSource: null,
  vendorId: null, linkBaseline: null, unlinkedNotice: false, billAmount: '', sourceLineId: null,
});

/** A non-negative number written with at most 4 decimals, or null when the text is not one ('' included). */
export function parseNonNegative(text: string | number | null | undefined): number | null {
  if (text === null || text === undefined) return null;
  const t = String(text).trim();
  if (t === '' || !/^\d*\.?\d+$|^\d+\.?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n <= 9999999999 ? Math.round(n * 10000) / 10000 : null;
}

const CENT_DIVISOR = BigInt(1000000);
const HALF_CENT = BigInt(500000);

/** quantity x price in whole cents (exact; half a cent up). 0 when either is missing / invalid. */
export function lineCents(quantity: string | number, unitPrice: string | number): number {
  const q = parseNonNegative(quantity);
  const p = parseNonNegative(unitPrice);
  if (q === null || p === null) return 0;
  const cents = (BigInt(Math.round(q * 10000)) * BigInt(Math.round(p * 10000)) + HALF_CENT) / CENT_DIVISOR;
  return Number(cents);
}

/** The line's amount in dollars (a blank or invalid price counts as 0). */
export const lineAmount = (line: Pick<LineItem, 'quantity' | 'unitPrice'>): number => lineCents(line.quantity, line.unitPrice) / 100;

/** The total of the lines, in dollars with 2 decimals (what the Line Items Section's single Total row shows and what the server stores). */
export function linesTotal(lines: Array<Pick<LineItem, 'quantity' | 'unitPrice'>>): number {
  return lines.reduce((sum, line) => sum + lineCents(line.quantity, line.unitPrice), 0) / 100;
}

/** The Bill Amount of a line in cents (a blank or invalid box counts as 0). */
export const billAmountCents = (line: Pick<LineItem, 'billAmount'>): number => {
  const amount = parseNonNegative(line.billAmount);
  return amount === null ? 0 : Math.round(amount * 100);
};

/** The total of a bill's lines: the sum of their Bill Amounts, in dollars. */
export const billLinesTotal = (lines: Array<Pick<LineItem, 'billAmount'>>): number => lines.reduce((sum, line) => sum + billAmountCents(line), 0) / 100;

/** Tax in whole cents: subtotal x tax %, half a cent up (tax % has at most 4 decimals) -- the same arithmetic as the server (po-lines.js). */
export const taxCents = (subtotalCents: number, taxPct: number): number =>
  Number((BigInt(subtotalCents) * BigInt(Math.round(taxPct * 10000)) + BigInt(500000)) / BigInt(1000000));

/** A percentage box ('' = 0) as a number between 0 and 100, or null when it is not one. */
export function parsePercent(text: string | number | null | undefined): number | null {
  if (text === null || text === undefined || String(text).trim() === '') return 0;
  const n = parseNonNegative(text);
  return n === null || n > 100 ? null : Math.round(n * 10000) / 10000;
}

/**
 * Subtotal + Tax + Other Amount = Total, in dollars with 2 decimals, exact in cents. The one preview calculation of every document with lines
 * (purchase orders and bills): the server repeats it and stores ITS result.
 */
export function documentTotals(subtotal: number, taxPct: string | number, otherAmount: string | number): { subtotal: number; tax: number; other: number; total: number } {
  const subtotalCents = Math.round(subtotal * 100);
  const tax = taxCents(subtotalCents, parsePercent(taxPct) ?? 0);
  const other = Math.round((parseNonNegative(otherAmount) ?? 0) * 100);
  return { subtotal: subtotalCents / 100, tax: tax / 100, other: other / 100, total: (subtotalCents + tax + other) / 100 };
}

export const formatMoney = (value: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);


/** The app's one wording for a blank required dropdown, for the Cost Code of a line: "Please select cost code". */
export const COST_CODE_REQUIRED_MESSAGE = requiredFieldMessage({ label: 'Cost Code', type: 'select' });

/** Every line of an item table has a cost code: why the lines cannot be saved (some line has none), or null. */
export const lineCostCodeProblem = (lines: Array<Pick<LineItem, 'costCodeId'>>): string | null => (lines.some((line) => !line.costCodeId) ? COST_CODE_REQUIRED_MESSAGE : null);

/**
 * The lines with the default cost code on every NEW line that has none yet (a line the user added before the company's cost codes finished loading).
 * Saved lines are never touched. Returns the same array when nothing needed filling.
 */
export function withDefaultCostCode<T extends Pick<LineItem, 'key' | 'costCodeId'>>(lines: T[], defaultCostCodeId: string): T[] {
  if (!defaultCostCodeId) return lines;
  const isNew = (line: T) => line.costCodeId === '' && /^line-\d+$/.test(line.key);
  return lines.some(isNew) ? lines.map((line) => (isNew(line) ? { ...line, costCodeId: defaultCostCodeId } : line)) : lines;
}
